import React from "react";
import { motion } from "framer-motion";
import Button from "./Button";
import { ShieldCheck, Cloud, Languages, Lock, Layers, Building2 } from "lucide-react";
import productHero from "../assets/product-hero.png";

const trust = [
  { icon: ShieldCheck, label: "ISO 27001" },
  { icon: Building2, label: "Enterprise ready" },
  { icon: Cloud, label: "Cloud native" },
  { icon: Languages, label: "Multilingual" },
  { icon: Lock, label: "Secure" },
  { icon: Layers, label: "Scalable" },
];

export default function Hero() {
  return (
    <section
      className="relative overflow-hidden pt-[112px] md:pt-[136px] pb-16"
      data-testid="hero-section"
    >
      {/* Gradient background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-purple via-brand-purpleMid to-brand-purpleLight" />
        <div className="absolute inset-0 bg-[radial-gradient(70%_60%_at_20%_10%,rgba(255,255,255,0.18),transparent_55%)]" />
        <div className="absolute -bottom-24 -right-24 w-[520px] h-[520px] rounded-full bg-brand-orange/30 blur-3xl" />
        <div className="absolute -top-40 -left-20 w-[520px] h-[520px] rounded-full bg-brand-purpleLight/30 blur-3xl" />
        <div className="noise-overlay opacity-70" />
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          {/* Copy */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-6"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/20 bg-white/10 backdrop-blur">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-orange animate-pulseGlow" />
              <span className="font-display text-[11px] uppercase tracking-[0.28em] text-white font-semibold">
                The hospitality operating system
              </span>
            </div>

            <h1
              className="mt-8 font-display text-white text-[46px] sm:text-[62px] md:text-[74px] leading-[0.98] tracking-tight text-balance"
              style={{ fontWeight: 800 }}
              data-testid="hero-headline"
            >
              One platform for{" "}
              <span className="grad-text">modern hotel</span> operations.
            </h1>

            <p
              className="mt-6 md:mt-7 text-white/90 text-[17px] md:text-[19px] leading-relaxed max-w-xl"
              data-testid="hero-subtitle"
            >
              Connect guests, hotel staff, and management through one
              intelligent hospitality platform designed to deliver exceptional
              guest experiences and operational excellence.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Button to="/demo" variant="primary" testId="hero-book-demo-btn">
                Book demo
              </Button>
              <Button
                to="/platform"
                variant="secondary"
                testId="hero-explore-platform-btn"
              >
                Explore platform
              </Button>
            </div>

            {/* Trust bar */}
            <div id="trust" className="mt-12" data-testid="trust-bar">
              <div className="font-display text-[10px] uppercase tracking-[0.28em] text-white/70 font-bold mb-4">
                Enterprise-grade — trusted by hospitality operators
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                {trust.map((t) => (
                  <div
                    key={t.label}
                    className="flex items-center gap-2 text-white/85"
                    data-testid={`trust-item-${t.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    <t.icon size={15} className="text-white" />
                    <span className="text-[13px] font-semibold">{t.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Product hero image */}
          <motion.div
            initial={{ opacity: 0, x: 40, y: 20 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ duration: 1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-6 relative"
          >
            <div className="relative aspect-[16/10] w-full">
              <div className="absolute -inset-6 rounded-3xl bg-white/5 blur-2xl" />
              {/* Solid brand-purple placeholder so the layout never collapses
                  if the image is still loading or hits a network hiccup. */}
              <div
                className="absolute inset-0 rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.6)]"
                style={{
                  background:
                    "linear-gradient(120deg, #4b328e 0%, #4b328e 62%, #d14f2a 62%, #d14f2a 100%)",
                }}
                aria-hidden
              />
              <img
                src={productHero}
                alt="iNPLASS platform — Guest, Staff, and HOP applications"
                loading="eager"
                decoding="async"
                fetchpriority="high"
                width={1600}
                height={1000}
                onError={(e) => {
                  // Fallback to the /public copy if the bundled URL fails.
                  if (!e.currentTarget.dataset.fallback) {
                    e.currentTarget.dataset.fallback = "1";
                    e.currentTarget.src = "/brand/product-hero.png";
                  }
                }}
                className="relative w-full h-full object-contain rounded-2xl"
                data-testid="hero-product-image"
                draggable={false}
              />
              {/* Floating badges */}
              <div className="hidden md:flex absolute -left-6 top-8 items-center gap-2 rounded-full bg-white/90 backdrop-blur px-3.5 py-2 shadow-xl">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulseGlow" />
                <span className="font-display text-[11px] uppercase tracking-[0.22em] text-brand-purple font-bold">
                  Live · 16 staff
                </span>
              </div>
              <div className="hidden md:flex absolute -right-4 bottom-10 items-center gap-2 rounded-full bg-brand-orange px-3.5 py-2 shadow-xl">
                <span className="font-display text-[11px] uppercase tracking-[0.22em] text-white font-bold">
                  SLA 89.8%
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
